import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import {
  ArrowRight, Briefcase,
  Check, Plus, Euro, Package, Puzzle, X, Loader2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

type PricingPlan = {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  monthly_base_price_cents: number;
  included_features: string[];
  is_active: boolean;
  sort_order: number;
};

type FeatureDef = {
  id: string;
  feature_key: string;
  label: string;
  description: string | null;
  category: string;
  target_group: string;
  monthly_price_cents: number;
  is_active: boolean;
  sort_order: number;
};

type RawPricingPlan = Omit<PricingPlan, 'included_features'> & {
  included_features: unknown;
};

const normalizeFeatureKeys = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((feature): feature is string => typeof feature === 'string') : [];

const formatCents = (cents: number) =>
  new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(cents / 100);

const PLAN_ACCENTS: Record<string, { border: string; bg: string; icon: string; badge: string }> = {
  basis: { border: 'border-gray-200', bg: 'bg-gray-50', icon: 'text-gray-600', badge: 'bg-gray-100 text-gray-700' },
  praxis: { border: 'border-blue-200', bg: 'bg-blue-50', icon: 'text-blue-600', badge: 'bg-blue-100 text-blue-700' },
  klinik: { border: 'border-purple-200', bg: 'bg-purple-50', icon: 'text-purple-600', badge: 'bg-purple-100 text-purple-700' },
};

export const PricingSection = () => {
  const [selectedPlanSlug, setSelectedPlanSlug] = useState<string>('praxis');
  const [selectedAddons, setSelectedAddons] = useState<string[]>([]);

  // Load pricing data from DB (public read policy)
  const { data: plans = [], isLoading: isLoadingPlans, isError: isPlansError } = useQuery<PricingPlan[]>({
    queryKey: ['public-pricing-plans'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('pricing_plans')
        .select('*')
        .eq('is_active', true)
        .order('sort_order');
      if (error) throw error;
      return ((data ?? []) as RawPricingPlan[])
        .filter(p => p.slug !== 'klinik')
        .map(p => ({
          ...p,
          included_features: normalizeFeatureKeys(p.included_features),
        }));
    },
  });

  const { data: featureDefs = [] } = useQuery<FeatureDef[]>({
    queryKey: ['public-feature-defs'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('feature_definitions')
        .select('*')
        .eq('is_active', true)
        .order('sort_order');
      if (error) throw error;
      return ((data ?? []) as FeatureDef[]).filter(f => f.target_group !== 'klinik');
    },
  });

  const selectedPlan = plans.find(p => p.slug === selectedPlanSlug);
  const availableAddons = featureDefs.filter(
    f => f.monthly_price_cents > 0 && !selectedPlan?.included_features.includes(f.feature_key)
  );
  const addonTotal = selectedAddons.reduce((sum, fk) => {
    const fd = featureDefs.find(f => f.feature_key === fk);
    return sum + (fd?.monthly_price_cents ?? 0);
  }, 0);
  const totalMonthly = (selectedPlan?.monthly_base_price_cents ?? 0) + addonTotal;

  return (
    <section id="preise" className="py-20 bg-secondary/20 border-y border-border/50">
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-primary/10 text-primary text-xs font-medium px-3 py-1.5 rounded-full mb-4">
            <Euro className="w-3 h-3" />
            Transparente Preise
          </div>
          <h2 className="text-2xl md:text-3xl font-bold text-foreground">
            Ihr Paket. Ihre Funktionen. Ihr Preis.
          </h2>
          <p className="text-muted-foreground mt-3 max-w-lg mx-auto">
            Wählen Sie ein Basispaket und buchen Sie nur die Zusatzfunktionen, die Sie wirklich brauchen.
            Keine versteckten Kosten.
          </p>
        </div>

        {/* Plan Cards */}
        {isLoadingPlans && (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
            <span className="ml-3 text-sm text-muted-foreground">Preise werden geladen...</span>
          </div>
        )}
        {isPlansError && (
          <div className="text-center py-12 bg-card rounded-2xl border border-red-200 p-6">
            <p className="text-sm text-red-600 font-medium">Preise konnten nicht geladen werden.</p>
            <p className="text-xs text-muted-foreground mt-1">Bitte versuchen Sie es später erneut oder kontaktieren Sie uns direkt.</p>
            <a href="mailto:info@icarus-health.de">
              <Button size="sm" className="mt-4 bg-primary hover:bg-primary/90 text-white rounded-xl">
                Kontakt aufnehmen
              </Button>
            </a>
          </div>
        )}
        {!isLoadingPlans && !isPlansError && plans.length > 0 && (
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4 md:gap-6 mb-12">
            {plans.map(plan => {
              const accent = PLAN_ACCENTS[plan.slug] ?? PLAN_ACCENTS.basis;
              const isSelected = plan.slug === selectedPlanSlug;
              const includedFeatures = featureDefs.filter(f =>
                plan.included_features.includes(f.feature_key)
              );

              return (
                <button
                  key={plan.id}
                  onClick={() => { setSelectedPlanSlug(plan.slug); setSelectedAddons([]); }}
                  className={`text-left rounded-2xl border-2 p-6 transition-all ${
                    isSelected
                      ? `${accent.border} ${accent.bg} shadow-md scale-[1.02]`
                      : 'border-border bg-card hover:shadow-sm hover:border-border/80'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-3">
                    {plan.slug === 'praxis'
                      ? <Briefcase className={`w-5 h-5 ${accent.icon}`} />
                      : <Package className={`w-5 h-5 ${accent.icon}`} />
                    }
                    <h3 className="font-bold text-lg">{plan.name}</h3>
                    {plan.slug === 'praxis' && (
                      <Badge className="bg-primary text-white text-xs ml-auto">Beliebt</Badge>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground mb-4">{plan.description}</p>
                  <div className="mb-4">
                    <span className="text-3xl font-bold text-foreground">
                      {formatCents(plan.monthly_base_price_cents)}
                    </span>
                    <span className="text-sm text-muted-foreground"> / Monat</span>
                    <p className="text-xs text-muted-foreground mt-0.5">zzgl. MwSt.</p>
                  </div>
                  <div className="space-y-2">
                    {includedFeatures.map(f => (
                      <div key={f.feature_key} className="flex items-center gap-2 text-sm">
                        <Check className="w-4 h-4 text-primary shrink-0" />
                        <span>{f.label}</span>
                      </div>
                    ))}
                  </div>
                  {isSelected && (
                    <div className={`mt-4 pt-3 border-t ${accent.border}`}>
                      <p className="text-xs font-medium text-primary">Ausgewählt</p>
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        )}

        {/* Addon Configurator */}
        {selectedPlan && availableAddons.length > 0 && (
          <div className="bg-card rounded-2xl border border-border p-6 md:p-8 mb-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center">
                <Puzzle className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <h3 className="font-bold text-lg">Zusatzfunktionen</h3>
                <p className="text-sm text-muted-foreground">
                  Erweiterungen individuell hinzubuchen - passend zum öffentlichen Praxis-MVP.
                </p>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-3">
              {availableAddons.map(addon => {
                const isSelected = selectedAddons.includes(addon.feature_key);
                return (
                  <button
                    key={addon.id}
                    onClick={() => {
                      setSelectedAddons(prev =>
                        isSelected
                          ? prev.filter(k => k !== addon.feature_key)
                          : [...prev, addon.feature_key]
                      );
                    }}
                    className={`flex items-center gap-3 p-4 rounded-xl border-2 text-left transition-all ${
                      isSelected
                        ? 'border-amber-300 bg-amber-50/50 shadow-sm'
                        : 'border-border hover:border-amber-200 hover:bg-amber-50/20'
                    }`}
                  >
                    {isSelected
                      ? <Check className="w-5 h-5 text-amber-600 shrink-0" />
                      : <Plus className="w-5 h-5 text-muted-foreground shrink-0" />
                    }
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium">{addon.label}</p>
                      <p className="text-xs text-muted-foreground truncate">{addon.description}</p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-sm font-bold text-foreground">
                        +{formatCents(addon.monthly_price_cents)}
                      </p>
                      <p className="text-xs text-muted-foreground">/Monat zzgl. MwSt.</p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Price Summary */}
        {selectedPlan && (
          <div className="bg-card rounded-2xl border-2 border-primary/20 p-6 md:p-8 max-w-2xl mx-auto">
            <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
              <Euro className="w-5 h-5 text-primary" />
              Ihre Konfiguration
            </h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center text-sm">
                <span className="font-medium">Basispaket "{selectedPlan.name}"</span>
                <span className="font-medium">{formatCents(selectedPlan.monthly_base_price_cents)}</span>
              </div>
              {selectedAddons.map(fk => {
                const fd = featureDefs.find(f => f.feature_key === fk);
                if (!fd) return null;
                return (
                  <div key={fk} className="flex justify-between items-center text-sm text-muted-foreground">
                    <span className="flex items-center gap-1.5">
                      <Plus className="w-3 h-3" />
                      {fd.label}
                    </span>
                    <div className="flex items-center gap-2">
                      <span>{formatCents(fd.monthly_price_cents)}</span>
                      <button
                        onClick={() => setSelectedAddons(prev => prev.filter(k => k !== fk))}
                        className="text-muted-foreground/50 hover:text-red-500 transition-colors"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                );
              })}
              <div className="border-t border-border pt-3 mt-3">
                <div className="flex justify-between items-center">
                  <span className="text-lg font-bold">Gesamt / Monat</span>
                  <span className="text-2xl font-bold text-primary">
                    {formatCents(totalMonthly)}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Alle Preise zzgl. MwSt. Monatlich kündbar.
                </p>
              </div>
            </div>
            <div className="mt-6 flex flex-wrap gap-3">
              <a href="mailto:info@icarus-health.de?subject=D%3AE%3AF%3AY%20Anfrage">
                <Button size="lg" className="bg-primary hover:bg-primary/90 text-white rounded-xl text-base px-6 h-12">
                  Angebot anfordern
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </a>
              <Link to="/login">
                <Button size="lg" variant="outline" className="rounded-xl text-base px-6 h-12 border-border">
                  Kostenlos testen
                </Button>
              </Link>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};
